import { useEffect, useRef } from 'react'
import { MapPinned, Paperclip, ShieldCheck, X } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DOCUMENT_TYPE_LABELS, ISSUER_LABELS, useAIIntake, type IntakeFields, type LotLookup } from './aiIntake'
import { AreaField, FieldRow, SectionTitle, TextField } from './ReviewBits'
import { areaFields, areaReading, areaUnitFields } from './area'
import { inputClass } from './reviewStyles'
import { isEmpty, type Row } from './reviewFields'
import { useCriterias, type Choice } from './criteria'

/**
 * The form's Land Details: the district, the mukim, the title type and the
 * proof of ownership. A land title carries all of it, so the AI usually
 * fills these in; the lists are the form's own, and the AI's reading is
 * matched against them by name.
 *
 * The district list is keyed to the state, the mukim list to the district,
 * exactly as the form nests them. The names carry a numeric prefix in the
 * database ("01 - Klang"), which is stripped before matching.
 */

/** "01 - Klang" is Klang; "Mukim Kapar" is Kapar. */
const plain = (name: unknown) =>
    String(name ?? '')
        .replace(/^\s*\d+\s*-\s*/, '')
        .replace(/\b(mukim|bandar|pekan|daerah)\b/gi, '')
        .trim()
        .toLowerCase()

const matches = (option: Choice, name: unknown) => {
    const a = plain(option.label)
    const b = plain(name)
    return Boolean(a && b && (a === b || a.includes(b) || b.includes(a)))
}

/** What MyLot showed, in a sentence the agent can check against the picture. */
const lookupLine = (found: LotLookup): string => {
    if (found.status === 'unavailable' && found.browser) return `Finding the land on ${found.source}…`
    const beside = found.searched.map((s) => s.lot).join(', ')
    if (found.status === 'lot') return `Found on ${found.source}${found.pa ? ` (${found.pa})` : ''}.`
    if (found.status === 'adjoining') return `Not on the plan. ${found.source} has lot ${found.lot} beside lot ${beside}: check it against the map.`
    if (found.status === 'within') {
        const acres = found.lotAreaSqm ? ` (${(found.lotAreaSqm / 4046.856).toLocaleString('en-MY', { maximumFractionDigits: 2 })} acres)` : ''
        return `This land is part of lot ${found.lot}${acres} on ${found.source}, which lies between lot ${beside}. Check it against the map.`
    }
    if (found.status === 'near') return `This land has no lot number on ${found.source}. It lies beside lot ${beside}, which is where the pin was placed.`
    return found.reason || `Not found on ${found.source}.`
}

/** The MyLot result under the lot number: what was found, and the map to check it against. */
function LotLookupNote({ found }: { found: LotLookup }) {
    const good = found.status === 'lot' || found.status === 'adjoining' || found.status === 'within' || found.status === 'near'
    return (
        <div className='mt-1.5 flex flex-col gap-1.5'>
            <p className='flex items-start gap-1 text-xs text-muted-foreground' title={found.detail ?? undefined}>
                <MapPinned className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${good ? 'text-emerald-600' : 'text-gray-400'}`} />
                <span>{lookupLine(found)}</span>
            </p>
            {found.imageUrl && (
                <a href={found.imageUrl} target='_blank' rel='noreferrer' title='Open the full map' className='block overflow-hidden rounded-md border border-gray-200'>
                    <img src={found.imageUrl} alt={`${found.source} map of lot ${found.shownLot ?? ''}`} className='h-36 w-full object-cover' loading='lazy' />
                </a>
            )}
        </div>
    )
}

export default function LandFields({ index, disabled, rows }: { index: number; disabled: boolean; rows: Row[] }) {
    const { drafts, sources, proofs, updateDraft, setProof } = useAIIntake()
    const { districts, subdistricts, landTitleTypes, landAreaUnits } = useCriterias()
    const proofInput = useRef<HTMLInputElement>(null)
    const draft = drafts[index]
    const f = draft.fields
    const proof = proofs[index] ?? null
    // What the AI made of the attached file, when it was one of the uploads.
    const proofSource = proof ? sources.find((s) => s.file === proof.name) : undefined

    const stateId = typeof f.stateId === 'number' ? f.stateId : null
    const inState = stateId ? districts.filter((d) => d.parentId === stateId) : districts
    const districtId = typeof f.districtId === 'number' ? f.districtId : inState.find((d) => matches(d, f.district))?.value ?? null
    const inDistrict = districtId ? subdistricts.filter((s) => s.parentId === districtId) : []
    const subdistrictId = typeof f.subdistrictId === 'number' ? f.subdistrictId : inDistrict.find((s) => matches(s, f.subdistrict ?? f.mukim))?.value ?? null
    // The form keeps the title type as the row's ID, filtered to the state.
    // The AI reads the prefix off the title ("HSD"), which names the row.
    const titlesInState = stateId ? landTitleTypes.filter((t) => t.parentId === stateId) : landTitleTypes
    const landTitleTypeId =
        typeof f.landTitleTypeId === 'number'
            ? f.landTitleTypeId
            : titlesInState.find((t) => plain(t.label.split('-')[0]) === plain(f.titleTypeCode))?.value ?? null

    const apply = (fields: IntakeFields) =>
        updateDraft(index, (d) => ({
            ...d,
            fields: { ...d.fields, ...fields },
            missingFields: d.missingFields.filter((k) => !Object.keys(fields).includes(k)),
        }))

    // A name the AI read that matches one of the form's rows is that row:
    // keep the ID on the draft, so the listing carries it and the review
    // stops asking for it.
    useEffect(() => {
        const settle: IntakeFields = {}
        if (districtId && typeof f.districtId !== 'number') {
            settle.districtId = districtId
            settle.district = inState.find((d) => d.value === districtId)?.label ?? null
        }
        if (subdistrictId && typeof f.subdistrictId !== 'number') {
            settle.subdistrictId = subdistrictId
            settle.subdistrict = inDistrict.find((x) => x.value === subdistrictId)?.label ?? null
        }
        if (landTitleTypeId && typeof f.landTitleTypeId !== 'number') settle.landTitleTypeId = landTitleTypeId
        if (Object.keys(settle).length) apply(settle)
        // Runs when a match becomes possible: the lists arrive, or the state changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [districtId, subdistrictId, landTitleTypeId, f.districtId, f.subdistrictId, f.landTitleTypeId])

    const select = (row: Row, value: string | null, list: { value: string | number; label: string }[], onPick: (v: string) => void, placeholder?: string) => (
        <FieldRow key={row.key} label={row.label} required={row.required}>
            <Select disabled={disabled || !list.length} value={value ?? undefined} onValueChange={onPick}>
                <SelectTrigger size='sm' className={`h-auto w-full ${inputClass(!value, row.required)}`}>
                    <SelectValue placeholder={placeholder ?? (row.required ? 'Required' : 'Not provided')} />
                </SelectTrigger>
                <SelectContent>
                    {list.map((c) => (
                        <SelectItem key={c.value} value={String(c.value)}>
                            {c.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </FieldRow>
    )

    return (
        <section>
            <SectionTitle>Land details</SectionTitle>
            <dl>
                {rows.map((row) => {
                    if (row.key === 'district') {
                        return select(row, districtId ? String(districtId) : null, inState, (v) => {
                            const chosen = inState.find((d) => d.value === Number(v))
                            apply({ district: chosen?.label ?? null, districtId: Number(v), subdistrict: null, subdistrictId: null })
                        }, stateId ? 'Required' : 'Pick the state first')
                    }
                    if (row.key === 'subdistrict') {
                        return select(row, subdistrictId ? String(subdistrictId) : null, inDistrict, (v) => {
                            const chosen = inDistrict.find((s) => s.value === Number(v))
                            apply({ subdistrict: chosen?.label ?? null, subdistrictId: Number(v) })
                        }, districtId ? 'Required' : 'Pick the daerah first')
                    }
                    if (row.key === 'titleTypeCode') {
                        return select(
                            row,
                            landTitleTypeId ? String(landTitleTypeId) : null,
                            titlesInState,
                            (v) => {
                                const chosen = titlesInState.find((t) => t.value === Number(v))
                                apply({ landTitleTypeId: Number(v), titleTypeCode: chosen ? chosen.label.split('-')[0].trim() : null })
                            },
                            // A state with no title types set up leaves the list empty; say so rather than show a dead field.
                            !stateId ? 'Pick the state first' : titlesInState.length ? 'Required' : 'No title types are set up for this state yet',
                        )
                    }
                    if (row.key === 'sizeOfLand') {
                        return (
                            <FieldRow key={row.key} label={row.label} required={row.required}>
                                <AreaField
                                    reading={areaReading(f, 'sizeOfLand', landAreaUnits)}
                                    units={landAreaUnits}
                                    required={row.required}
                                    disabled={disabled}
                                    onChange={(value, unit) => apply(areaFields('sizeOfLand', value, unit))}
                                    onUnit={(unit) => apply(areaUnitFields('sizeOfLand', f, unit))}
                                />
                            </FieldRow>
                        )
                    }
                    if (row.key === 'proof') {
                        return (
                            <FieldRow key='proof' label={row.label} required>
                                <div className='flex flex-col gap-1'>
                                <div className='flex items-center gap-2'>
                                    <button
                                        type='button'
                                        disabled={disabled}
                                        onClick={() => proofInput.current?.click()}
                                        className={`flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-sm transition-colors hover:border-gray-400 disabled:cursor-not-allowed ${
                                            proof ? 'border-gray-200' : 'border-red-300 text-red-600'
                                        }`}>
                                        <Paperclip className='h-3.5 w-3.5' />
                                        {proof ? 'Replace' : 'Choose file'}
                                    </button>
                                    {proof && (
                                        <span className='flex min-w-0 items-center gap-1 text-sm'>
                                            <span className='truncate'>{proof.name}</span>
                                            <button type='button' aria-label='Remove proof' onClick={() => setProof(index, null)} className='cursor-pointer rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-black'>
                                                <X className='h-3 w-3' />
                                            </button>
                                        </span>
                                    )}
                                </div>
                                {proofSource && (
                                    <p className='flex items-start gap-1 text-xs text-muted-foreground' title={proofSource.proofReason ?? proofSource.description}>
                                        <ShieldCheck className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${proofSource.proofOfOwnership ? 'text-emerald-600' : 'text-gray-400'}`} />
                                        <span>
                                            {DOCUMENT_TYPE_LABELS[proofSource.documentType] ?? 'Document'}
                                            {proofSource.issuer && proofSource.issuer !== 'unknown' ? ` · ${ISSUER_LABELS[proofSource.issuer]}` : ''}
                                            {proofSource.proofOfOwnership ? ' · attached from your uploads' : ' · check this is an official document'}
                                        </span>
                                    </p>
                                )}
                                </div>
                                <input
                                    ref={proofInput}
                                    type='file'
                                    accept='.pdf,.jpg,.jpeg,.png,.webp'
                                    className='hidden'
                                    onChange={(e) => {
                                        const file = e.target.files?.[0]
                                        if (file) setProof(index, file)
                                        e.target.value = ''
                                    }}
                                />
                            </FieldRow>
                        )
                    }
                    return (
                        <FieldRow key={row.key} label={row.label} required={row.required}>
                            <TextField
                                value={isEmpty(f[row.key]) ? '' : String(f[row.key])}
                                required={row.required}
                                disabled={disabled}
                                onCommit={(raw) => apply({ [row.key]: raw.trim() || null })}
                            />
                            {row.key === 'lotNo' && draft.lotLookup && <LotLookupNote found={draft.lotLookup} />}
                        </FieldRow>
                    )
                })}
            </dl>
        </section>
    )
}
